// ============================================================================
// API — Cupones (panel-cliente).
// ============================================================================

import { supabaseClient } from '@plataforma/supabase-client';
import { tieneFeature } from '@plataforma/utils';

import { obtenerEstadoSesion } from '../estado-sesion.js';

function empresaIdActual() {
  const sesion = obtenerEstadoSesion();
  return sesion.claims?.empresa_id ?? sesion.empresa?.id ?? null;
}

function empresaActual() {
  const sesion = obtenerEstadoSesion();
  return sesion.empresa ?? null;
}

function requiereCupones(operacion) {
  const empresa = empresaActual();
  if (!tieneFeature(empresa, 'cupones')) {
    return Promise.resolve({
      data: null,
      error: {
        message: 'La funcionalidad Cupones no está disponible en tu plan. Mejora a Medio o Pro.',
        plan_requerido: 'Medio',
      },
    });
  }
  return operacion();
}

const COLUMNAS_LISTADO =
  'id, codigo, tipo, valor, compra_minima, fecha_inicio, fecha_fin, ' +
  'usos_maximos, usos_actuales, limite_por_cliente, aplicacion, ' +
  'productos_ids, categorias_ids, combinable, estado, creado_en, actualizado_en';

/**
 * @param {{ estado?: 'activo' | 'inactivo', busqueda?: string }} [filtros]
 */
export async function listarCupones(filtros = {}) {
  return requiereCupones(() => {
    const empresaId = empresaIdActual();
    let consulta = supabaseClient()
      .from('cupon')
      .select(COLUMNAS_LISTADO)
      .eq('empresa_id', empresaId)
      .is('eliminado_en', null)
      .order('creado_en', { ascending: false });

    if (filtros.estado) consulta = consulta.eq('estado', filtros.estado);
    if (filtros.busqueda) consulta = consulta.ilike('codigo', `%${filtros.busqueda}%`);

    return consulta;
  });
}

/**
 * @param {object} datos
 * @param {string} datos.codigo
 * @param {'porcentaje'|'fijo'} datos.tipo
 * @param {number} datos.valor
 * @param {number} [datos.compra_minima]
 * @param {string} [datos.fecha_inicio]
 * @param {string} [datos.fecha_fin]
 * @param {number} [datos.usos_maximos]
 * @param {number} [datos.limite_por_cliente]
 * @param {'todos'|'productos'|'categorias'} [datos.aplicacion]
 * @param {string[]} [datos.productos_ids]
 * @param {string[]} [datos.categorias_ids]
 * @param {boolean} [datos.combinable]
 */
export async function crearCupon(datos) {
  return requiereCupones(() => {
    const empresaId = empresaIdActual();
    return supabaseClient()
      .from('cupon')
      .insert({
        empresa_id: empresaId,
        codigo: datos.codigo.trim().toUpperCase(),
        tipo: datos.tipo,
        valor: datos.valor,
        compra_minima: datos.compra_minima ?? null,
        fecha_inicio: datos.fecha_inicio ?? null,
        fecha_fin: datos.fecha_fin ?? null,
        usos_maximos: datos.usos_maximos ?? null,
        limite_por_cliente: datos.limite_por_cliente ?? 1,
        aplicacion: datos.aplicacion ?? 'todos',
        productos_ids: datos.aplicacion === 'productos' ? datos.productos_ids : null,
        categorias_ids: datos.aplicacion === 'categorias' ? datos.categorias_ids : null,
        combinable: Boolean(datos.combinable),
        estado: 'activo',
      })
      .select(COLUMNAS_LISTADO)
      .single();
  });
}

/**
 * @param {string} cuponId
 * @param {object} cambios
 */
export async function actualizarCupon(cuponId, cambios) {
  return requiereCupones(() => {
    const empresaId = empresaIdActual();
    const actualizacion = { ...cambios };

    if (actualizacion.codigo) actualizacion.codigo = actualizacion.codigo.trim().toUpperCase();
    if (actualizacion.aplicacion === 'productos') actualizacion.categorias_ids = null;
    if (actualizacion.aplicacion === 'categorias') actualizacion.productos_ids = null;

    return supabaseClient()
      .from('cupon')
      .update(actualizacion)
      .eq('id', cuponId)
      .eq('empresa_id', empresaId)
      .is('eliminado_en', null)
      .select(COLUMNAS_LISTADO)
      .single();
  });
}

/**
 * @param {string} cuponId
 */
export async function eliminarCupon(cuponId) {
  return requiereCupones(() => {
    const empresaId = empresaIdActual();
    return supabaseClient()
      .from('cupon')
      .update({ eliminado_en: new Date().toISOString() })
      .eq('id', cuponId)
      .eq('empresa_id', empresaId)
      .is('eliminado_en', null);
  });
}

/**
 * @param {string} cuponId
 */
export async function activarCupon(cuponId) {
  return actualizarCupon(cuponId, { estado: 'activo' });
}

/**
 * @param {string} cuponId
 */
export async function desactivarCupon(cuponId) {
  return actualizarCupon(cuponId, { estado: 'inactivo' });
}
